"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

/**
 * The chat widget is heavy (~ tens of kB of JS, animations, dynamic styles) and
 * not needed for first paint. We:
 *   1. Skip SSR so the markup never leaks into the initial HTML.
 *   2. Wait for `requestIdleCallback` (or a short timeout fallback) before
 *      importing the module, so it doesn't compete with hydration or LCP.
 *
 * The exported component renders nothing until ready, then mounts the real
 * widget. Visible behaviour is unchanged.
 */
const ProslavaChatWidget = dynamic(
  () =>
    import("@/components/ProslavaChatWidget").then(
      (mod) => mod.ProslavaChatWidget,
    ),
  { ssr: false },
);

export function ProslavaChatWidgetLazy() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    const mount = () => {
      if (!cancelled) setReady(true);
    };

    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    }).requestIdleCallback;

    if (typeof ric === "function") {
      const id = ric(mount, { timeout: 2500 });
      return () => {
        cancelled = true;
        const cic = (window as unknown as {
          cancelIdleCallback?: (id: number) => void;
        }).cancelIdleCallback;
        if (typeof cic === "function") cic(id);
      };
    }

    const t = window.setTimeout(mount, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, []);

  if (!ready) return null;
  return <ProslavaChatWidget />;
}
